import { isApprovedStatus } from "../models/evidenceItem";
import type {
  EvidenceAttachmentModel,
  SupplementableEvidence,
  SupplementaryDocumentKind,
} from "../models/evidenceAttachment";

/** Spejler MAX_ATTACHMENTS_PER_EVIDENCE i cle-compliance-api. */
export const MAX_ATTACHMENTS = 5;

/** Spejler MAX_UPLOAD_FILE_BYTES i cle-compliance-api. */
export const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

/** Det, der skiller bilagskortet på et kursusbevis fra kortet på en
 * undervisning (ADV-359). Reglerne er de samme og håndhæves ét sted i
 * cle-compliance-api; det er kun ordene, der følger beviset. */
export type SupplementaryDocumentationTexts = {
  /** Bevisets navn i bestemt form, først i en sætning. */
  definite: string;
  /** Til fejlen, når grænsen er nået: "pr. kursusbevis". */
  perDocument: string;
  /** Til beskeden, når kortet er lukket: "på dette kursusbevis". */
  onDocument: string;
  /** Kortets tekst, før der er vedhæftet noget. */
  emptyIntro: string;
  /** Noten under upload-knappen. */
  uploadNote: string;
  /** id på "Vælg fil"-knappen. */
  addButtonId: string;
};

export const SUPPLEMENTARY_DOCUMENTATION_TEXTS: Record<
  SupplementaryDocumentKind,
  SupplementaryDocumentationTexts
> = {
  kursusbevis: {
    definite: "Kursusbeviset",
    perDocument: "pr. kursusbevis",
    onDocument: "på dette kursusbevis",
    emptyIntro:
      "Mener du, at valideringen er forkert, kan du vedhæfte materiale, der understøtter kursusbeviset — fx kursusprogram, bekræftelse fra udbyderen eller dokumentation for navneændring.",
    // Sagde før ADV-359, at sekretariatet først fik besked, når medlemmet
    // skrev til dem. Det har ikke passet siden cle-compliance-api#62, hvor en
    // upload selv skriver en linje i beskedtråden.
    uploadNote:
      'Bilag bliver ikke analyseret automatisk og giver ikke i sig selv lektioner. Når du vedhæfter et bilag, får sekretariatet besked, og du kan følge sagen under "Besked til sekretariatet".',
    addButtonId: "cle-add-course-certificate-attachment",
  },
  // Undervisning analyseres slet ikke automatisk, så noten siger ikke noget om
  // det. Teksterne afventer sekretariatets godkendelse sammen med ADV-343.
  undervisning: {
    definite: "Undervisningen",
    perDocument: "pr. undervisning",
    onDocument: "på denne undervisning",
    emptyIntro:
      "Du kan vedhæfte materiale, der understøtter undervisningen, fx kursusprogram, invitation eller bekræftelse fra arrangøren.",
    uploadNote:
      'Bilagene indgår i sekretariatets gennemgang af undervisningen. Når du vedhæfter et bilag, får sekretariatet besked, og du kan følge sagen under "Besked til sekretariatet".',
    addButtonId: "cle-add-teaching-attachment",
  },
};

export type AttachmentRejection =
  "UNSUPPORTED_FILE_TYPE" | "FILE_TOO_LARGE" | "ATTACHMENT_LIMIT_EXCEEDED";

const REJECTION_MESSAGES: Record<
  Exclude<AttachmentRejection, "ATTACHMENT_LIMIT_EXCEEDED">,
  string
> = {
  UNSUPPORTED_FILE_TYPE: "Bilag skal være en PDF.",
  FILE_TOO_LARGE: "Bilag må højst fylde 5 MB.",
};

export const getAttachmentRejectionMessage = (
  rejection: AttachmentRejection,
  kind: SupplementaryDocumentKind,
): string =>
  rejection === "ATTACHMENT_LIMIT_EXCEEDED"
    ? `Du kan højst vedhæfte ${MAX_ATTACHMENTS} bilag ${SUPPLEMENTARY_DOCUMENTATION_TEXTS[kind].perDocument}.`
    : REJECTION_MESSAGES[rejection];

/** Widened past File, så testene kan sende almindelige objekter - node har
 * ingen File. Samme greb som planCourseCertificateUpload. */
type AttachmentFileLike = Pick<File, "name" | "size" | "type">;

const isPdf = (file: AttachmentFileLike): boolean =>
  file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

/** Den samme kontrol som serveren, kørt først, så medlemmet får svaret med det
 * samme i stedet for efter en upload. Serveren er stadig den, der bestemmer. */
export const resolveAttachmentRejection = (
  file: AttachmentFileLike,
  currentAttachmentCount: number,
): AttachmentRejection | null => {
  if (currentAttachmentCount >= MAX_ATTACHMENTS) {
    return "ATTACHMENT_LIMIT_EXCEEDED";
  }

  if (!isPdf(file)) {
    return "UNSUPPORTED_FILE_TYPE";
  }

  if (file.size > MAX_ATTACHMENT_BYTES) {
    return "FILE_TOO_LARGE";
  }

  return null;
};

export const getAttachments = (
  evidence: SupplementableEvidence | undefined,
): EvidenceAttachmentModel[] => evidence?.attachments ?? [];

/** Om kortet overhovedet skal vises.
 *
 * Bemærk at det bliver stående, når medlemmet HAR vedhæftet bilag, også efter
 * at upload er lukket: ellers ville dokumentationen forsvinde fra siden i det
 * øjeblik beviset blev indberettet, uden at nogen havde fjernet den. */
export const shouldShowSupplementaryCard = (
  evidence: SupplementableEvidence | undefined,
): boolean => {
  if (!evidence) {
    return false;
  }

  return (
    evidence.canUploadSupplementaryDocumentation === true ||
    getAttachments(evidence).length > 0
  );
};

/** Om bilag må fjernes: så længe beviset stadig er åbent for bilag.
 *
 * Bevidst IKKE canUploadMoreAttachments, som også er falsk, når grænsen på fem
 * er nået - præcis der, hvor medlemmet har mest brug for at fjerne et. Og
 * bevidst ikke bare !isReported: efter en godkendelse er dokumentationen bag
 * afgørelsen ikke medlemmets at trække tilbage, og kortet siger allerede, at
 * der hverken kan tilføjes eller fjernes.
 *
 * Serveren er lempeligere (den spærrer kun indberettede beviser), så det her er
 * den strammere af de to. */
export const canDeleteAttachments = (
  evidence: SupplementableEvidence | undefined,
): boolean => evidence?.canUploadSupplementaryDocumentation === true;

/** Hvad kortet siger, når der ikke kan tilføjes flere bilag.
 *
 * To forskellige grunde, som indtil nu delte den samme sætning: grænsen er
 * nået (og så KAN man stadig fjerne), eller vinduet er lukket (og så kan man
 * ikke). Den gamle tekst sagde "kan ikke tilføjes eller fjernes" i begge
 * tilfælde og modsagde dermed sletteknappen ved siden af. */
export const getAttachmentsClosedMessage = (
  evidence: SupplementableEvidence | undefined,
  kind: SupplementaryDocumentKind,
): string => {
  const texts = SUPPLEMENTARY_DOCUMENTATION_TEXTS[kind];

  if (canDeleteAttachments(evidence)) {
    return `Du har vedhæftet det højeste antal bilag (${MAX_ATTACHMENTS}). Fjern et, hvis du vil vedhæfte et andet.`;
  }

  if (evidence?.isReported) {
    return `${texts.definite} er indberettet. Bilagene bliver stående som dokumentation.`;
  }

  if (evidence && isApprovedStatus(evidence.status)) {
    return `${texts.definite} er godkendt. Bilagene bliver stående som dokumentation for afgørelsen.`;
  }

  return `Der kan ikke tilføjes eller fjernes bilag ${texts.onDocument}.`;
};

export const canUploadMoreAttachments = (
  evidence: SupplementableEvidence | undefined,
): boolean => {
  if (!evidence?.canUploadSupplementaryDocumentation) {
    return false;
  }

  return getAttachments(evidence).length < MAX_ATTACHMENTS;
};
